import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiHelpCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className={`border-b border-white/5 transition-colors ${isOpen ? 'bg-white/[0.02]' : ''}`}>
      <button 
        onClick={onClick} 
        className="w-full py-8 flex items-center justify-between text-left group px-6"
      >
        <span className={`text-lg md:text-xl font-black uppercase tracking-tighter transition-colors ${isOpen ? 'text-[#CCFF00]' : 'text-white'}`}>
          {question}
        </span>
        <motion.div 
          animate={{ rotate: isOpen ? 180 : 0 }}
          className={`w-10 h-10 flex items-center justify-center border ${isOpen ? 'border-[#CCFF00] text-[#CCFF00]' : 'border-white/10 text-slate-500'} group-hover:border-[#CCFF00] transition-colors`}
        >
          <SafeIcon icon={FiChevronDown} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-10 pt-2 px-6 max-w-3xl">
              <div className="text-slate-400 text-lg leading-relaxed font-medium">
                {answer}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "Which leagues and tournaments do you cover?",
      answer: "We cover major European domestic leagues, continental tournaments, and international football events. Our editorial desk monitors the Premier League, La Liga, Serie A, Bundesliga, Ligue 1, and the Champions League in real-time."
    },
    {
      question: "How do the Stat Matrix widgets work?",
      answer: "The Stat Matrix widgets are generated as ready-to-use HTML code. You simply insert the provided HTML snippet directly into your newsfeed articles, and the engine handles the live data rendering and visualization automatically."
    },
    {
      question: "Can we use our own affiliate links in the 'Where to Watch' widgets?",
      answer: "Absolutely. The Broadcast Locators allow you to inject your own tracking parameters and affiliate IDs for streaming services or betting partners, creating a direct revenue stream from your news feed."
    },
    {
      question: "Are images included in the news feed?",
      answer: "No. Due to complex international copyright regulations and varying licensing agreements across sports photography agencies, images are not included in our standard news feed. We provide the text and data, allowing you to pair the content with your own licensed imagery."
    },
    {
      question: "How does the pricing model work?",
      answer: "The Pitch Feed operates on a transparent flat-fee model of €999 per month per language. This includes unlimited access to all articles and standard widgets generated in your selected language market."
    },
    {
      question: "How do we get started with Addons?",
      answer: "Extensions like the Stat Matrix and Broadcast Locators can be enabled by contacting our technical desk. Our team will assist with the integration process and configure the regional rights settings for the broadcasting widgets."
    }
  ];

  return (
    <section className="py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-20">
          <div className="lg:col-span-1">
            <div className="sticky top-40">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-[#CCFF00] flex items-center justify-center text-black">
                  <SafeIcon icon={FiHelpCircle} className="text-xl" />
                </div>
                <div>
                  <h2 className="text-[#CCFF00] font-mono-data text-[10px] font-black uppercase tracking-[0.4em]">Intelligence</h2>
                  <p className="text-white font-black uppercase tracking-tighter">Protocol v2.4</p>
                </div>
              </div>
              <h3 className="text-5xl md:text-6xl font-black text-white tracking-tighter uppercase leading-[0.9] mb-8">
                Frequently <br />
                <span className="text-transparent" style={{ WebkitTextStroke: '1px #CCFF00' }}>Asked</span> <br />
                Questions
              </h3>
              <p className="text-slate-500 text-sm font-medium leading-relaxed max-w-xs">
                Technical and operational details regarding our global news syndication engine and expansion modules.
              </p>
            </div>
          </div>

          <div className="lg:col-span-2 border-t border-white/5">
            {faqs.map((faq, index) => (
              <FAQItem 
                key={index} 
                question={faq.question} 
                answer={faq.answer} 
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;